<template>
  <div
    :id="`theme:${theme._id}`"
    class="playlist-layer theme"
    v-if="theme"
    :class="[{ 'empty' : !posts.length }, { 'editing': isEditing }]"
    v-on:click="requestLayerChange"
  >
    <div class="layer-info">

      <button
        class="drag-handle tb-btn tb-btn-link sm low-em"
        title="Rearrange"
      >
        <span class="icon icon-drag-handle" />
      </button>

      <div class="layer-title margin-left-xs">
        <div class="title-text">{{displayTitle}}</div>
      </div>

      <div class="layer-description capitalize margin-left-xxs">
        <div class="description-text">{{theme.layout}}</div>
      </div>

      <preview-live-switch :item='item'></preview-live-switch>

      <dropdown position="left" class="more-actions margin-left-xxs">
        <template slot="trigger">
          <button class="tb-btn tb-btn-text sm medium-em" title="More actions">
            <span class="icon icon-more-vertical" />
          </button>
        </template>

        <template slot="dropdown-body">
          <div
            class="dropdown-list-item"
            @click="duplicateItem"
          >
            <div class="dropdown-list-item-label">
              Duplicate in Playlist
            </div>
          </div>

          <hr>

          <div
            v-if="posts.length"
            class="dropdown-list-item"
            @click="clearPosts"
          >
            <div class="dropdown-list-item-label warning">
              Clear Posts
            </div>
          </div>

          <div
            class="dropdown-list-item"
            @click="removeItem"
          >
            <div class="dropdown-list-item-label warning">
              Remove
            </div>
          </div>
        </template>
      </dropdown>
    </div>

    <div class="layer-rundown">
      <draggable
        ref="tgbDropArea"
        class="layer-draggable draggable tgbPostDrop tgbTagboardDrop tgbDropArea scroll-x"
        @change="onChange"
        @start="onStart"
        :move="checkMove"
        @unchoose="onUnchoose"
        v-model="posts"
        revert-on-spill="true"
        v-bind="{
          group: { name: 'posts', put: check },
          animation: 200,
          touchStartThreshold: 10,
        }"
      >
        <component
          :is="getType(post)"
          v-for="(post, index) in posts"
          :key="index"
          :layout="item"
          :item="post"
          :parent="item"
          :queuedPost="queuedPost"
          @select="selectThemePost"
          @delete="removeThemePost"
          @play-video="playVideo"
          @toggle-video="togglePlaying"
          @play-preview-video="playVideo(true)"
          @toggle-preview-video="togglePlaying(true)"
        />

        <div class="tgbDropAreaHelp" v-if="!posts.length">
          <p>Drag and drop a tagboard or posts here</p>
        </div>
      </draggable>
    </div>
  </div>

  <div
    v-else class="playlist-layer deleted"
    :class="{ active: (isCurrentLayout || isCurrentSamplePost) }"
  >
    <div class="layer-info">
      <div class="deleted-message">
        This Theme <span v-if="displayTitle">({{displayTitle}})</span> has been deleted.
      </div>
      <button
        class="tb-btn tb-btn-text sm medium-em margin-left-xxs"
        title="Remove item from playlist"
        v-on:click.stop.prevent="removeItem"
      >
        <i class="icon icon-minus-circle"></i>
      </button>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import TimelineMixins from '../../../mixins/timeline-mixins';
import TimelinePostComponent from '../post/TimelinePost.vue';
import TimelinePostPanelComponent from '../post-panel/TimelinePostPanel.vue';
import PreviewLiveSwitch from '../../PreviewLiveSwitch.vue';

let { events } = require('@tagboard/lib-analytics');

events = events({ plansAPI: process.env.PLANS_API });

export default {
  name: 'TimelineLayoutComponent',
  mixins: [TimelineMixins],
  props: ['item'],

  components: {
    post: TimelinePostComponent,
    panel: TimelinePostPanelComponent,
    draggable,
    'preview-live-switch': PreviewLiveSwitch,
  },

  data() {
    return {
      error: false,
    };
  },

  computed: {
    production() {
      return this.$store.state.production.production;
    },

    posts: {
      get() {
        return this.item.posts;
      },

      set(posts) {
        this.$store.dispatch('setPosts', {
          posts,
          timelineId: this.item.timeline_id,
        });
      },
    },

    queuedPost() {
      return this.item.queuedPost;
    },

    theme() {
      return this.$store.state.themes.lookupTable[this.item._id];
    },

    displayTitle() {
      return this.item?.title || this.theme?.title || '';
    },
  },
  methods: {
    playVideo(preview) {
      if (preview) {
        this.$store.dispatch('playPreviewVideo');
      } else {
        this.$store.dispatch('playPostVideo');
      }
    },

    togglePlaying(preview) {
      if (preview) {
        this.$store.dispatch('togglePreviewVideo');
      } else {
        this.$store.dispatch('toggleLiveVideo');
      }
    },

    getType(item) {
      if (item?.layout === 'panel') {
        return 'panel';
      }

      return 'post';
    },

    clearPosts() {
      this.posts = [];
    },

    onStart() {
      const dropzones = document.querySelectorAll('.tgbTagboardDrop,.tgbPostDrop');
      for (let i = 0; i < dropzones.length; i += 1) {
        dropzones[i].classList.add('tgbDropArea--active');
      }
    },

    checkMove(e) {
      if (this.$refs.tgbDropArea.$el === e.to.closest('.tgbDropArea')) {
        // skip the other checks if we are reordering
        return true;
      }

      const { type, post_id: postId } = e.draggedContext.element;
      const hasContent = Boolean(e.relatedContext.list.length);
      const isInList = e.relatedContext.list.some((i) => i.post_id === postId);
      const notAllowed = (hasContent && type === 'tagboard') || isInList;
      const errorMessages = {
        tagboard: 'A full tagboard cannot be presented with other content.',
        duplicate: 'Duplicate posts not allowed.',
      };

      if (notAllowed) {
        e.to.classList.add('tgbDropArea--no');
        e.dragged.style.cursor = 'not-allowed';
        this.error = errorMessages[hasContent && type === 'tagboard' ? 'tagboard' : 'duplicate'];
        return false;
      }

      this.error = false;
      return true;
    },

    onUnchoose(e) {
      e.item.style.cursor = ('pointer');

      if (this.error) {
        this.errorToast(this.error);
      }

      document.querySelectorAll('.tgbDropArea--no').forEach((el) => {
        el.classList.remove('tgbDropArea--no');
        this.error = false;
      });

      document.querySelectorAll('.tgbDropArea--active').forEach((el) => {
        el.classList.remove('tgbDropArea--active');
      });
    },

    onChange(e) {
      if (!e || !e.added) {
        return;
      }

      const el = e.added.element;

      events.log({
        event: 'Producer Dragged Content From Tagboard',
        fields: {
          production_id: this.production._id,
          content_type: el.network,
          source_tagboard: el.from_tagboard,
          destination_theme: this.item.layout,
        },
        ownerId: this.production.owner,
      });
    },

    check(to, from, el) {
      if (this.$route.name === 'tagboards' || this.$route.name === 'smartpanels') {
        return true;
      }

      if (this.$route.name === 'graphics' || this.$route.name === 'saved-graphics' || this.$route.name === 'themes' || $(el).hasClass('playlist-layer')) {
        return false;
      }

      const $el = $(el);
      let classes;
      const $post = $el.hasClass('grid-cell') ? $(el).find('.post-wrapper') : $el;

      if (this.item.layout === 'stories') {
        switch (this.theme.settings.network) {
          case 'snapchat':
            return $post.hasClass('snapchat');
          case 'instagram':
            return $post.hasClass('instagram-stories');
          default:
            return false;
        }
      } else {
        classes = $post.attr('class').split(/\s+/);
        return classes.indexOf('snapchat') === -1 && classes.indexOf('instagram-stories') === -1;
      }
    },
  },
};
</script>
